<template>
  <div class="sku-insight">
    <div class="sku-kpi-grid" :class="{ 'is-single-line': categoryCards.length }">
      <button
        v-for="item in categoryCards"
        :key="'type-' + item.itemKey"
        type="button"
        class="sku-kpi"
        :class="[
          'supply-card--' + String(item.itemKey || 'OTHER').toLowerCase(),
          { on: selectedCategory === item.itemKey },
        ]"
        data-supply-drawer-keep
        :title="typeCardTip(item)"
        @click="$emit('category', item.itemKey)"
      >
        <div class="label">{{ item.label }} LOW/EMPTY</div>
        <div class="value">{{ item.lowEmpty | currency }}</div>
      </button>
      <button
        v-if="!isSupplyGroup"
        type="button"
        class="sku-kpi"
        data-supply-drawer-keep
        @click="$emit('kpi', 'sku')"
      >
        <div class="label">{{ groupCountLabel }}</div>
        <div class="value">{{ skuCount | currency }}</div>
      </button>
      <button
        type="button"
        class="sku-kpi danger"
        data-supply-drawer-keep
        :title="errorSlotTip"
        @click="$emit('kpi', 'error')"
      >
        <div class="label">
          {{ '에러 슬롯' | translate }}
          <span class="th-help">?</span>
        </div>
        <div class="value">{{ errorCount | currency }}</div>
      </button>
      <button
        v-if="!categoryCards.length"
        type="button"
        class="sku-kpi"
        data-supply-drawer-keep
        :title="t('현재 LOW 또는 EMPTY인 슬롯 수입니다. 한 품번이 여러 칸이면 칸마다 세며, 대시보드 LOW/EMPTY 슬롯과 같습니다.')"
        @click="$emit('kpi', 'lowEmpty')"
      >
        <div class="label">{{ 'LOW/EMPTY 슬롯' | translate }}</div>
        <div class="value">{{ lowEmptyCount | currency }}</div>
      </button>
      <button type="button" class="sku-kpi" data-supply-drawer-keep @click="$emit('kpi', 'urgent')">
        <div class="label">{{ '긴급 수량' | translate }}</div>
        <div class="value">{{ urgentCount | currency }}</div>
      </button>
    </div>
    <p class="sku-hint">{{ hintText }}</p>
    <div class="sku-tools">
      <span class="sku-search-label">{{ '현재 목록 검색' | translate }}</span>
      <input
        :value="keyword"
        type="text"
        class="sku-input"
        :placeholder="searchPlaceholder"
        @input="$emit('update:keyword', $event.target.value)"
        @keydown.enter.prevent="$emit('search')"
      />
      <button type="button" class="sku-btn" @click="$emit('search')">
        {{ '검색' | translate }}
      </button>
    </div>
    <div class="sku-table-wrap" data-supply-drawer-keep>
      <table class="sku-table">
        <thead>
          <tr class="group-row">
            <th :colspan="basicColspan" class="group-h">{{ '기본정보' | translate }}</th>
            <th colspan="6" class="group-h">{{ '현재 상태' | translate }}</th>
            <th colspan="4" class="group-h">{{ '교체 이력·예측' | translate }}</th>
            <th v-if="isSupplyGroup" colspan="2" class="group-h">{{ '관리' | translate }}</th>
          </tr>
          <tr>
            <th
              v-if="!isSupplyGroup"
              class="sortable sticky-col"
              :class="sortThClass('groupLabel')"
              @click="sortBy('groupLabel')"
            >
              {{ groupColumnLabel }}{{ sortMark('groupLabel') }}
            </th>
            <th
              v-if="groupBy === 'MODEL'"
              class="sortable"
              :class="sortThClass('manufacturerName')"
              @click="sortBy('manufacturerName')"
            >
              {{ '제조사' | translate }}{{ sortMark('manufacturerName') }}
            </th>
            <th v-if="isSupplyGroup" class="sortable sticky-col" :class="sortThClass('partNumber')" @click="sortBy('partNumber')">
              {{ '대표 품번' | translate }}{{ sortMark('partNumber') }}
            </th>
            <th v-if="isSupplyGroup" class="sortable" :class="sortThClass('installedPartNumber')" @click="sortBy('installedPartNumber')">
              {{ '실제 품번' | translate }}{{ sortMark('installedPartNumber') }}
            </th>
            <th v-if="isSupplyGroup">{{ '소모품 기본정보' | translate }}</th>
            <th v-if="isSupplyGroup" class="sortable" :class="sortThClass('compatibilityType')" @click="sortBy('compatibilityType')">
              {{ '유형' | translate }}{{ sortMark('compatibilityType') }}
            </th>
            <th v-if="isSupplyGroup" class="sortable" :class="sortThClass('installedManufacturer')" @click="sortBy('installedManufacturer')">
              {{ '업체' | translate }}{{ sortMark('installedManufacturer') }}
            </th>
            <th
              class="sortable num"
              :class="sortThClass('printerCount')"
              :title="t('이 행에 해당하는 프린터 수입니다. 단위는 대입니다.')"
              @click="sortBy('printerCount')"
            >
              {{ '프린터 수' | translate }}{{ sortMark('printerCount') }}
            </th>
            <th
              class="sortable num"
              :class="sortThClass('activeCount')"
              :title="slotHeaderTip"
              @click="sortBy('activeCount')"
            >
              {{ '장착 슬롯 수' | translate }}{{ sortMark('activeCount') }}
              <span class="th-help">?</span>
            </th>
            <th class="sortable num" :class="sortThClass('avgRemainPercent')" @click="sortBy('avgRemainPercent')">
              {{ '평균잔량' | translate }}{{ sortMark('avgRemainPercent') }}
            </th>
            <th class="sortable num" :class="sortThClass('errorCount')" :title="errorSlotTip" @click="sortBy('errorCount')">
              {{ '에러' | translate }}{{ sortMark('errorCount') }}
              <span class="th-help">?</span>
            </th>
            <th class="sortable num" :class="sortThClass('lowEmpty')" @click="sortBy('lowEmpty')">
              {{ 'LOW/EMPTY' | translate }}{{ sortMark('lowEmpty') }}
            </th>
            <th class="sortable num" :class="sortThClass('urgentCount')" @click="sortBy('urgentCount')">
              {{ '긴급' | translate }}{{ sortMark('urgentCount') }}
            </th>
            <th
              class="sortable num"
              :class="sortThClass('replaceCount')"
              :title="replaceCountHeaderTip"
              @click="sortBy('replaceCount')"
            >
              {{ '교체수' | translate }}{{ sortMark('replaceCount') }}
              <span class="th-help">?</span>
            </th>
            <th
              class="sortable num"
              :class="sortThClass('avgCycleDays')"
              :title="avgCycleHeaderTip"
              @click="sortBy('avgCycleDays')"
            >
              {{ '평균주기' | translate }}{{ sortMark('avgCycleDays') }}
              <span class="th-help">?</span>
            </th>
            <th
              class="sortable num"
              :class="sortThClass('monthlyForecast')"
              :title="monthlyForecastHeaderTip"
              @click="sortBy('monthlyForecast')"
            >
              {{ '월간예측' | translate }}{{ sortMark('monthlyForecast') }}
              <span class="th-help">?</span>
            </th>
            <th class="num">{{ '다음 교체' | translate }}</th>
            <th v-if="isSupplyGroup">{{ '임대업체' | translate }}</th>
            <th v-if="isSupplyGroup">{{ '공용 품번' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!rows.length">
            <td :colspan="colspan" class="empty">{{ '데이터가 없습니다.' | translate }}</td>
          </tr>
          <tr v-for="(row, idx) in rows" :key="row.rowKey || idx">
            <td v-if="!isSupplyGroup" class="sticky-col">{{ row.groupLabel || row.supplyLabel || '-' }}</td>
            <td v-if="groupBy === 'MODEL'">{{ row.manufacturerName || '-' }}</td>
            <td v-if="isSupplyGroup" class="sticky-col">{{ row.familyPartNumber || row.partNumber || '-' }}</td>
            <td v-if="isSupplyGroup">{{ actualPartNumber(row) }}</td>
            <td v-if="isSupplyGroup" class="info-cell" :title="supplyInfo(row)">{{ supplyInfo(row) }}</td>
            <td
              v-if="isSupplyGroup"
              :class="{ muted: isMutedText(formatCompatibility(row.compatibilityType)) }"
            >
              {{ formatCompatibility(row.compatibilityType) }}
            </td>
            <td v-if="isSupplyGroup" :class="{ muted: isMutedText(formatVendor(row)) }">
              {{ formatVendor(row) }}
            </td>
            <td class="num">
              <button
                type="button"
                class="num-link"
                :title="t('이 소모품이 장착된 프린터 목록')"
                @click="$emit('detail', { row, kind: 'printer' })"
              >
                {{ row.printerCount || 0 }}{{ '대' | translate }}
              </button>
            </td>
            <td class="num">
              <button
                type="button"
                class="num-link"
                :title="drawerOpenTip"
                @click="$emit('detail', { row, kind: 'slot' })"
              >
                {{ row.activeCount || 0 }}{{ '개' | translate }}
              </button>
            </td>
            <td class="num">
              <button
                type="button"
                class="num-link"
                :title="drawerOpenTip"
                @click="$emit('detail', { row, kind: 'remain' })"
              >
                {{ formatRemain(row.avgRemainPercent) }}
              </button>
            </td>
            <td class="num" :class="{ danger: Number(row.errorCount || 0) > 0 }">
              <button
                type="button"
                class="num-link"
                :title="drawerOpenTip"
                @click="$emit('detail', { row, kind: 'error' })"
              >
                {{ row.errorCount || 0 }}{{ '개' | translate }}
              </button>
            </td>
            <td class="num">
              <button
                type="button"
                class="num-link"
                :title="drawerOpenTip"
                @click="$emit('detail', { row, kind: 'lowEmpty' })"
              >
                {{ Number(row.lowCount || 0) + Number(row.emptyCount || 0) }}{{ '개' | translate }}
              </button>
            </td>
            <td class="num" :class="{ warn: Number(row.urgentCount || 0) > 0 }">
              <button
                type="button"
                class="num-link"
                :title="drawerOpenTip"
                @click="$emit('detail', { row, kind: 'urgent' })"
              >
                {{ row.urgentCount || 0 }}{{ '개' | translate }}
              </button>
            </td>
            <td class="num">
              <button
                type="button"
                class="num-link"
                :title="replaceCountCellTip(row)"
                @click="$emit('detail', { row, kind: 'replace' })"
              >
                {{ row.replaceCount || 0 }}{{ '회' | translate }}
              </button>
            </td>
            <td class="num" :class="{ muted: row.avgCycleDays == null }">
              <button
                type="button"
                class="num-link"
                :title="avgCycleCellTip(row)"
                @click="$emit('detail', { row, kind: 'cycle' })"
              >
                {{ formatCycleDays(row.avgCycleDays) }}
              </button>
            </td>
            <td class="num" :class="{ muted: row.monthlyForecast == null }">
              <button
                type="button"
                class="num-link"
                :title="monthlyForecastCellTip(row)"
                @click="$emit('detail', { row, kind: 'forecast' })"
              >
                {{ formatMonthlyForecast(row.monthlyForecast) }}
              </button>
            </td>
            <td class="num" :class="{ muted: isMutedText(formatNextReplace(row)) }">
              <button
                type="button"
                class="num-link"
                :title="drawerOpenTip"
                @click="$emit('detail', { row, kind: 'next' })"
              >
                {{ formatNextReplace(row) }}
              </button>
            </td>
            <td v-if="isSupplyGroup">
              <button
                type="button"
                class="num-link"
                :title="t('이 품번의 임대업체별 랭킹')"
                @click.stop="$emit('rental-rank', row)"
              >
                {{ row.rentalCompanyCount || 0 }}
              </button>
            </td>
            <td v-if="isSupplyGroup">
              <button
                type="button"
                class="map-link-btn"
                data-supply-drawer-keep
                @click.stop="$emit('map', row)"
              >
                {{ '매핑' | translate }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="sku-paging">
      <button type="button" class="sku-btn outline" :disabled="page <= 1" @click="$emit('page-change', page - 1)">
        {{ '이전' | translate }}
      </button>
      <span>{{ page }} / {{ totalPages }}</span>
      <button
        type="button"
        class="sku-btn outline"
        :disabled="page >= totalPages"
        @click="$emit('page-change', page + 1)"
      >
        {{ '다음' | translate }}
      </button>
    </div>
  </div>
</template>

<script>
import NumberFormatUtil from '@/assets/plugins/mps-common/formatters/NumberFormatUtil'

export default {
  name: 'SupplySkuInsightPanel',
  mixins: [NumberFormatUtil],
  props: {
    rows: { type: Array, default: () => [] },
    groupBy: { type: String, default: 'SUPPLY' },
    keyword: { type: String, default: '' },
    totalCount: { type: Number, default: 0 },
    errorCount: { type: Number, default: 0 },
    lowEmptyCount: { type: Number, default: 0 },
    urgentCount: { type: Number, default: 0 },
    page: { type: Number, default: 1 },
    rowsPerPage: { type: Number, default: 30 },
    orderBy: { type: String, default: 'errorCount' },
    orderDir: { type: String, default: 'DESC' },
    includeUnmapped: { type: Boolean, default: false },
    viewMode: { type: String, default: 'compare' },
    categoryCards: { type: Array, default: () => [] },
    selectedCategory: { type: String, default: '' },
  },
  computed: {
    isCompareView() {
      return true
    },
    skuCount() {
      return this.totalCount || this.rows.length
    },
    pageUrgentCount() {
      return this.rows.reduce((sum, row) => sum + Number(row.urgentCount || 0), 0)
    },
    totalPages() {
      return Math.max(1, Math.ceil((this.totalCount || this.rows.length || 0) / (this.rowsPerPage || 30)))
    },
    isSupplyGroup() {
      return this.groupBy === 'SUPPLY'
    },
    groupCountLabel() {
      if (this.groupBy === 'MODEL') return this.t('모델 수')
      if (this.groupBy === 'ACCOUNT') return this.t('거래처 수')
      if (this.groupBy === 'RENTAL') return this.t('임대업체 수')
      return this.t('SKU 수')
    },
    groupColumnLabel() {
      if (this.groupBy === 'MODEL') return this.t('프린터 모델')
      if (this.groupBy === 'RENTAL') return this.t('임대업체')
      return this.t('구분')
    },
    drawerOpenTip() {
      return this.t('우측 상세창에서 대상 목록을 엽니다.')
    },
    slotHeaderTip() {
      return this.t(
        '프린터에서 수집된 소모품 장착 자리의 수입니다. 프린터 1대에 여러 개가 있을 수 있습니다. 예를 들어 컬러 복합기 1대에서 검정·파랑·빨강·노랑 토너가 각각 수집되면 프린터 수는 1대, 장착 슬롯 수는 4개로 표시됩니다.',
      )
    },
    errorSlotTip() {
      return this.t(
        '현재 장착된 소모품 중 시스템의 에러 판정 기준에 해당하는 슬롯 수입니다. 누르면 우측 상세창이 열립니다. 잔량이 비어 있어도 포함합니다. 수집 잔량(현재/최대)이나 퍼센트에 음수가 있으면 에러로 세지 않습니다. 예: -2/-1, 0/-1, -2/1.',
      )
    },
    basicColspan() {
      if (this.groupBy === 'MODEL') return 2
      if (this.isSupplyGroup) return 5
      return 1
    },
    hintText() {
      return this.t(
        '현재 상태(프린터 수, 장착 슬롯 수, 잔량, 에러, LOW/EMPTY, 긴급)는 지금 장착 기준입니다. 교체수·평균주기·월간예측은 교체 이력입니다. 계산 방법은 항목의 ?에서 확인합니다.',
      )
    },
    replaceCountHeaderTip() {
      return this.t(
        '선택한 조회 기간 동안 실제로 수집된 교체 횟수입니다. 지금 장착된 슬롯 수와는 다릅니다. 슬롯이 많아도 기간 중 교체가 거의 없으면 교체수는 작습니다.',
      )
    },
    avgCycleHeaderTip() {
      return this.t(
        '교체가 2회 이상일 때 첫 교체일과 마지막 교체일 사이를 (교체수 − 1)로 나눈 일수입니다. 소모품별은 최근 일별 집계 값을 쓰고, 없으면 선택한 조회 기간의 교체 이력으로 같은 식을 계산합니다. 프린터 모델별·임대업체별은 선택한 조회 기간의 교체 이력으로 계산합니다.',
      )
    },
    monthlyForecastHeaderTip() {
      return this.t(
        '30 ÷ 평균주기(일)입니다. 평균주기와 같은 출처를 씁니다. 소모품별은 최근 일별 집계가 있으면 그 값을 쓰고, 없으면 선택한 조회 기간으로 계산합니다. 프린터 모델별·임대업체별은 선택한 조회 기간입니다. 예: 평균주기 1일 → 30 ÷ 1 = 30회/월.',
      )
    },
    searchPlaceholder() {
      if (this.groupBy === 'MODEL') return this.t('프린터 모델 / 제조사 검색')
      if (this.groupBy === 'RENTAL') return this.t('임대업체 검색')
      return this.t('품번 / 제조사 / 유형 검색')
    },
    colspan() {
      if (this.groupBy === 'MODEL') return 12
      if (this.groupBy === 'RENTAL') return 11
      return 17
    },
  },
  methods: {
    t(text) {
      return this.$options.filters && this.$options.filters.translate
        ? this.$options.filters.translate(text)
        : text
    },
    isMutedText(text) {
      const value = String(text || '')
      return value.indexOf(this.t('미지정')) >= 0 || value.indexOf(this.t('데이터 부족')) >= 0
    },
    sortBy(field) {
      this.$emit('sort', field)
    },
    sortThClass(field) {
      return { active: this.orderBy === field }
    },
    sortMark(field) {
      if (this.orderBy !== field) return ''
      return this.orderDir === 'ASC' ? ' ▲' : ' ▼'
    },
    formatCompatibility(type) {
      const v = String(type || '').toUpperCase()
      if (v === 'OEM' || v === 'EXACT') return this.t('정품')
      if (v === 'COMPATIBLE') return this.t('호환')
      if (v === 'REMANUFACTURED') return this.t('재생')
      if (v === 'MANUAL') return this.t('직접입력')
      if (v === 'UNKNOWN' || !v) return this.t('미지정')
      return type
    },
    formatForecast(value) {
      if (value === null || value === undefined || value === '') return this.t('데이터 부족')
      return value
    },
    formatCycleDays(value) {
      if (value === null || value === undefined || value === '') return this.t('데이터 부족')
      return `${value}${this.t('일')}`
    },
    formatMonthlyForecast(value) {
      if (value === null || value === undefined || value === '') return this.t('데이터 부족')
      return `${value}${this.t('회')}`
    },
    replaceCountCellTip(row) {
      const count = Number(row.replaceCount || 0)
      const slots = Number(row.activeCount || 0)
      return this.t(
        `조회 기간 교체 ${count}회입니다. 현재 슬롯 ${slots}개와는 다른 값입니다.`,
      )
    },
    avgCycleCellTip(row) {
      const count = Number(row.replaceCount || 0)
      const cycle = row.avgCycleDays
      if (cycle === null || cycle === undefined || cycle === '') {
        return this.t('교체가 2회 미만이면 간격을 나눌 수 없어 평균주기를 계산하지 않습니다.')
      }
      return this.t(
        `첫 교체~마지막 교체 간격을 (교체수 ${count} − 1)로 나눈 값입니다. 약 ${cycle}일마다 한 번 교체된 셈입니다.`,
      )
    },
    monthlyForecastCellTip(row) {
      const cycle = row.avgCycleDays
      const forecast = row.monthlyForecast
      if (forecast === null || forecast === undefined || forecast === '') {
        return this.t('평균주기가 없으면 월간예측도 계산하지 않습니다.')
      }
      return this.t(
        `30 ÷ 평균주기 ${cycle}일 = ${forecast}회/월. 같은 교체 속도가 한 달 이어진다고 가정한 예측입니다.`,
      )
    },
    formatRemain(value) {
      if (value === null || value === undefined || value === '') return '-'
      return `${Number(value).toLocaleString()}%`
    },
    formatNextReplace(row) {
      if (Number(row.urgentCount || 0) > 0) return this.t('즉시')
      return row.nextReplaceDate || this.t('데이터 부족')
    },
    formatVendor(row) {
      const installed = String(row?.installedManufacturer || '').trim()
      if (installed) return installed
      return this.t('미지정')
    },
    actualPartNumber(row) {
      return String(row.installedPartNumber || row.partNumber || '').trim() || '-'
    },
    typeCardTip(item) {
      return this.t(
        '이 유형의 LOW/EMPTY 슬롯입니다. 누르면 그 유형만 목록에 남습니다. 프린터 {0}, 전체 슬롯 {1}.',
      )
        .replace('{0}', Number(item.printerCount || 0).toLocaleString())
        .replace('{1}', Number(item.slotCount || 0).toLocaleString())
    },
    supplyInfo(row) {
      const raw = String(row.description || '')
      const lower = raw.toLowerCase()
      const stored = String(row.supplyCategory || '').trim().toUpperCase()
      const inkWord = /(^|[^a-z])ink([^a-z]|$)/i.test(raw) || raw.includes('잉크')
      const blocked =
        lower.includes('toner') || raw.includes('토너') || lower.includes('waste') || raw.includes('폐토너')
      const resolved =
        stored === 'INK' || ((stored === 'TONER' || stored === 'OTHER' || !stored) && inkWord && !blocked)
          ? 'INK'
          : stored || 'OTHER'
      const catMap = {
        TONER: this.t('토너'),
        INK: this.t('잉크'),
        DRUM: this.t('드럼'),
        WASTE: this.t('폐토너'),
        OTHER: this.t('기타'),
      }
      const cat = catMap[resolved] || resolved || ''
      const color =
        row.colorCode && String(row.colorCode).toUpperCase() !== 'UNKNOWN' ? row.colorCode : ''
      const desc = String(row.description || '').trim()
      const head = [cat, color].filter(Boolean).join(' ')
      if (head && desc) return `${head} · ${desc}`
      return desc || head || row.supplyLabel || '-'
    },
  },
}
</script>

<style scoped>
.sku-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.sku-kpi-grid.is-single-line {
  flex-wrap: nowrap;
  overflow-x: auto;
}
.sku-kpi.on {
  border-color: #249ebf;
  background: #e8f6fa;
  box-shadow: 0 0 0 2px rgba(36, 158, 191, 0.12);
}
.supply-card--toner .value { color: #249ebf; }
.supply-card--ink .value { color: #0f766e; }
.supply-card--drum .value { color: #7c3aed; }
.supply-card--waste .value { color: #ea580c; }
.supply-card--other .value { color: #64748b; }
.sku-kpi {
  height: 34px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 0 10px;
  cursor: pointer;
  font: inherit;
  color: #334155;
  white-space: nowrap;
}
.sku-kpi:hover {
  border-color: #2563eb;
  background: #f8fafc;
}
.sku-kpi.warn .value {
  color: #b45309;
}
.sku-kpi.danger .value {
  color: #dc2626;
}
.sku-kpi .label {
  font-size: 13px;
  color: #475569;
}
.sku-kpi .value {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}
.sku-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.45;
}
.sku-tools {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.sku-search-label {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
  white-space: nowrap;
}
.sku-input {
  min-width: 220px;
  height: 34px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0 10px;
  font-size: 13px;
}
.sku-chips {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
}
.sku-chip {
  height: 34px;
  border: 1px solid #cbd5e1;
  border-radius: 16px;
  background: #fff;
  color: #475569;
  padding: 0 12px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
}
.sku-chip.on {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
}
.sku-btn {
  height: 34px;
  border: 0;
  border-radius: 4px;
  background: #2563eb;
  color: #fff;
  padding: 0 12px;
  cursor: pointer;
  font-weight: 600;
}
.sku-table-wrap {
  overflow: auto;
  max-width: 100%;
  max-height: calc(100vh - 320px);
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}
.sku-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}
.sku-table th,
.sku-table td {
  box-sizing: border-box;
  height: 38px;
  padding: 0 10px;
  border-bottom: 1px solid #e2e8f0;
  border-right: 1px solid #f1f5f9;
  text-align: left;
  white-space: nowrap;
  background: #fff;
  vertical-align: middle;
}
.sku-table th {
  position: sticky;
  top: 28px;
  z-index: 2;
  background: #f8fafc;
  color: #334155;
  font-size: 13px;
  font-weight: 700;
}
.sku-table thead tr.group-row th {
  top: 0;
  z-index: 3;
  height: 28px;
  text-align: center;
  background: #eef2f6;
  color: #0f172a;
}
.sku-table th.sticky-col,
.sku-table td.sticky-col {
  position: sticky;
  left: 0;
  z-index: 4;
  min-width: 140px;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 1px 0 0 #e2e8f0;
}
.sku-table th.sticky-col {
  z-index: 5;
  background: #f8fafc;
}
.sku-table td.num,
.sku-table th.num {
  text-align: right;
}
.sku-table td.muted,
.sku-table td.muted .num-link {
  color: #94a3b8;
}
.sku-table td.warn .num-link {
  color: #b45309;
  font-weight: 700;
}
.sku-table th.sortable {
  cursor: pointer;
  user-select: none;
}
.th-help {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid #94a3b8;
  color: #64748b;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  vertical-align: middle;
}
.sku-table th.sortable:hover,
.sku-table th.sortable.active {
  color: #2563eb;
}
.sku-table td.empty {
  text-align: center;
  color: #94a3b8;
  padding: 24px;
}
.sku-table td.danger {
  color: #dc2626;
  font-weight: 700;
}
.sku-table td.danger .num-link {
  color: #dc2626;
}
.sku-table td.info-cell {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.num-link {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 36px;
  border: 0;
  background: transparent;
  padding: 0 2px;
  color: inherit;
  cursor: pointer;
  font: inherit;
}
.num-link:hover {
  background: #eff6ff;
  text-decoration: underline;
  color: #2563eb;
}
.sku-paging {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
  font-size: 13px;
  color: #475569;
}
.sku-btn.outline {
  background: #fff;
  color: #2563eb;
  border: 1px solid #2563eb;
}
.sku-btn.outline:disabled {
  opacity: 0.5;
  cursor: default;
}
.map-link-btn {
  border: 1px solid #93c5fd;
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  cursor: pointer;
}
.map-link-btn:hover {
  background: #dbeafe;
}
</style>
